// src/hooks/usePriceApi.tsx

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { SensorId } from '@/types/domain'
import { PriceCollectionResponse, PricePool } from '@/types/domain/price'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

// context

type PriceContextType = {
  data: PricePool
  setData: Dispatch<SetStateAction<PricePool>>
  refresh: (_opts?: { force?: boolean }) => Promise<PricePool>
}

const PriceCtx = createContext<PriceContextType>({
  data: [],
  setData: () => {},
  refresh: async () => [],
})

type Props = {
  children: ReactNode
}

export const PriceDataProvider = ({ children }: Props) => {
  const [prices, setPrices] = useState<PricePool>([])
  const pricesReqRef = useRef<Promise<PricePool> | null>(null)

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        await refreshToken()
        return await fn()
      } catch (error: any) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          try {
            await refreshToken()
            return await fn()
          } catch (e2: any) {
            e2.status = e2?.status ?? e2?.response?.status ?? 401
            throw e2
          }
        }

        throw {
          message: error?.message || String(error),
          status,
        }
      }
    },
    []
  )

  const fetchPrices = useCallback(
    async ({ force = false }: { force?: boolean } = {}): Promise<PricePool> => {
      if (pricesReqRef.current && !force) return pricesReqRef.current

      pricesReqRef.current = (async () => {
        try {
          const data = await withRequest<PricePool>(async () => {
            const url = `${REACT_APP_API_URL}/sensor_price/admin/sensor_price`
            const options = { method: 'GET', headers: { ...authHeader() } }
            const response = await fetch(url, options)

            if (response.status === 204) return []

            if (!response.ok) {
              const text = await response.text().catch(() => '')
              throw {
                message: text || `HTTP ${response.status}`,
                status: response.status,
              }
            }

            const payload: PriceCollectionResponse = await response.json()

            return payload?.data ?? []
          })

          if (Array.isArray(data)) setPrices(data)
          return Array.isArray(data) ? data : []
        } finally {
          pricesReqRef.current = null
        }
      })()

      return pricesReqRef.current
    },
    [withRequest]
  )

  useEffect(() => {
    if (prices.length > 0) return
    ;(async () => {
      try {
        await fetchPrices()
      } catch (error) {
        const statusCode = error?.status ?? error?.response?.status
        notifyError(`PriceApi: GET prices ${statusCode}`)
      }
    })()
  }, [prices.length, fetchPrices])

  const ctxValue = useMemo(
    () => ({
      data: prices,
      setData: setPrices,
      refresh: fetchPrices,
    }),
    [prices, fetchPrices]
  )

  return <PriceCtx.Provider value={ctxValue}>{children}</PriceCtx.Provider>
}

// pool

export const usePricePool = (): PricePool => {
  const ctx = useContext(PriceCtx)
  return ctx?.data ?? []
}

// api

export const usePriceApi = () => {
  const { refresh: refreshPrices } = useContext(PriceCtx)

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        await refreshToken()
        return await fn()
      } catch (error: any) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          try {
            await refreshToken()
            return await fn()
          } catch (e2: any) {
            e2.status = e2?.status ?? e2?.response?.status ?? 401
            throw e2
          }
        }

        throw {
          message: error?.message || String(error),
          status,
        }
      }
    },
    []
  )

  type PriceMutation = {
    entity_id?: number
    sensor_id: SensorId
    price_for_year: number
    price: number
  }

  const recordData = useCallback((data: PriceMutation) => {
    const { entity_id, sensor_id, price_for_year, price } = data

    return {
      data: {
        attributes: {
          entity_id,
          sensor_id,
          price_for_year,
          price,
        },
      },
    }
  }, [])

  const save = useCallback(
    async (data: PriceMutation): Promise<number | null> =>
      withRequest<number | null>(async () => {
        const { sensor_id } = data
        if (!sensor_id) {
          notifyError(LC.PRICE_SENSOR_REQUIRED)
          return null
        }

        const url = `${REACT_APP_API_URL}/sensor_price/admin/sensor_price`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(recordData(data)),
        }

        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        const result: { id?: number } = await response.json()

        notifySuccess(LC.PRICE_SAVED)
        await refreshPrices({ force: true })

        return result?.id ?? null
      }),
    [recordData, refreshPrices, withRequest]
  )

  const update = useCallback(
    async (data: PriceMutation): Promise<number | null> =>
      withRequest<number | null>(async () => {
        const { sensor_id } = data
        if (!sensor_id) {
          notifyError(LC.PRICE_SENSOR_REQUIRED)
          return null
        }

        const url = `${REACT_APP_API_URL}/sensor_price/admin/sensor_price`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'PATCH',
          body: JSON.stringify(recordData(data)),
        }

        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        const result: { id?: number } = await response.json()

        notifySuccess(LC.PRICE_UPDATED)
        await refreshPrices({ force: true })

        return result?.id ?? null
      }),
    [recordData, refreshPrices, withRequest]
  )

  const remove = useCallback(
    async (id: number): Promise<boolean> =>
      withRequest<boolean>(async () => {
        if (!id) {
          notifyError(LC.PRICE_ID_REQUIRED)
          return false
        }

        const url = `${REACT_APP_API_URL}/sensor_price/admin/sensor_price/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'DELETE',
        }

        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        notifySuccess(LC.PRICE_DELETED)

        await refreshPrices({ force: true })
        return true
      }),
    [refreshPrices, withRequest]
  )

  return { save, update, remove, refreshPrices }
}
